<script setup lang="ts">
import { useModalStore } from '@/stores/modal';
import { storeToRefs } from 'pinia';
import { watch, onMounted, ref } from 'vue';
import MindShareModal from '@/components/modalTemplate/MindShareModal.vue';
import MindShareDeleteModal from '@/components/modalTemplate/MindShareDeleteModal.vue';
import DharmaEditModal from '@/components/modalTemplate/DharmaEditModal.vue';
import MindShareResetModal from '@/components/modalTemplate/MindShareResetModal.vue';
import MindShareChoiceRoomModal from '@/components/modalTemplate/MindShareChoiceRoomModal.vue';
import MeditationEditModal from '@/components/modalTemplate/MeditationEditModal.vue';
import NestedMindshareEditModal from '@/components/modalTemplate/NestedMindshareEditModal.vue';
import { IMeditationRoom } from '@/types/meditation';
import VolunteerConfirmModal from '@/components/modalTemplate/VolunteerConfirmModal.vue';
import VolunteerNoticeModal from '@/components/modalTemplate/VolunteerNoticeModal.vue';
import QnaEditModal from '@/components/modalTemplate/QnaEditModal.vue';
import QnaDeleteModal from '@/components/modalTemplate/QnaDeleteModal.vue';
import InviteDeleteModal from '@/components/modalTemplate/InviteDeleteModal.vue';
import ImageViewerModal from '@/components/modalTemplate/ImageViewerModal.vue';
import SimpleAlertModal from '@/components/modalTemplate/SimpleAlertModal.vue';
import LoginHelpModal from '@/components/modalTemplate/LoginHelpModal.vue';
import PolicyReadModal from '@/components/modalTemplate/PolicyReadModal.vue';
import ReturnApplicationModal from '@/components/modalTemplate/ReturnApplicationModal.vue';

const modalStore = useModalStore();
const { isOpen, type, props } = storeToRefs(modalStore);

// 중첩 모달을 위한 상태 추가
const nestedModal = ref<{
  isOpen: boolean;
  type: NestedModalType | null;
  props: NestedModalProps | null;
}>({
  isOpen: false,
  type: null,
  props: null,
});

// 중첩 모달 열기 함수
const openNestedModal = (modalType: NestedModalType, modalProps: NestedModalProps) => {
  nestedModal.value = {
    isOpen: true,
    type: modalType,
    props: modalProps,
    // onConfirm: modalProps?.onConfirm || null,
    // isEdit: modalProps?.isEdit || false,
  };
};

// 중첩 모달 닫기 함수
const closeNestedModal = () => {
  nestedModal.value = {
    isOpen: false,
    type: null, // null로 변경하여 타입 에러 해결
    props: null,
  };
};

// modalComponents의 타입을 더 명확하게 정의
const modalComponents: Record<string, any> = {
  'mind-share': MindShareModal,
  'mind-share-delete': MindShareDeleteModal,
  'dharma-edit': DharmaEditModal,
  'mind-share-reset': MindShareResetModal,
  'mind-share-choice-room': MindShareChoiceRoomModal,
  'meditation-edit': MeditationEditModal,
  'nested-mindshare-edit': NestedMindshareEditModal,
  'volunteer-confirm': VolunteerConfirmModal,
  'volunteer-notice': VolunteerNoticeModal,
  'qna-edit': QnaEditModal,
  'qna-delete': QnaDeleteModal,
  'invite-delete': InviteDeleteModal,
  'image-viewer': ImageViewerModal,
  'simple-alert': SimpleAlertModal,
  'login-help': LoginHelpModal,
  'policy-read': PolicyReadModal,
  'return-application': ReturnApplicationModal,
} as const;

// type ModalProps = InstanceType<(typeof modalComponents)[keyof typeof modalComponents]>['$props'];

interface NestedMindshareEditModalProps {
  selectedRoomItem: IMeditationRoom;
  onConfirm: (selectedRoomItem: IMeditationRoom) => void;
  mode: 'edit' | 'create';
}

type NestedModalProps = NestedMindshareEditModalProps | null;
type NestedModalType = 'nested-mindshare-edit' | null;
</script>

<template>
  <!-- 기존 모달 -->
  <component
    v-if="isOpen && type"
    :is="modalComponents[type]"
    v-bind="props"
    :is-open="isOpen"
    @close="modalStore.close"
    @open-nested-modal="openNestedModal"
    @close-nested-modal="closeNestedModal"
  />
  <component
    v-if="nestedModal.isOpen && nestedModal.type"
    :is="modalComponents[nestedModal.type]"
    v-bind="nestedModal.props as NestedModalProps"
    :is-open="nestedModal.isOpen"
    @close="closeNestedModal"
  />
</template>
